
import { X, Package, User, CalendarBlank, ChatText } from "phosphor-react";
import { motion, AnimatePresence } from "framer-motion";

const STATUS_UI = {
  confirmed: {
    label: "Confirmée",
    cls: "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 border-yellow-500/20",
  },
  preparing: {
    label: "En préparation",
    cls: "bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20",
  },
  shipped: {
    label: "Remise au transporteur",
    cls: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 border-indigo-500/20",
  },
  delivered: {
    label: "Livrée au client",
    cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
  },
  cancelled: {
    label: "Annulée",
    cls: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20",
  },
  refused: {
    label: "Refusée",
    cls: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20",
  },
  no_answer: {
    label: "Pas de réponse",
    cls: "bg-neutral-500/10 text-neutral-700 dark:text-neutral-300 border-neutral-500/20",
  },
};
export default function OrderDetailsModal({ isOpen, onClose, order }) {
  if (!order) return null;

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatMAD = (amount) => {
    const n = Number(amount);
    const safe = Number.isFinite(n) ? n : 0;
    return new Intl.NumberFormat("fr-MA", {
      style: "currency",
      currency: "MAD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safe);
  };

  const statusUI =
    STATUS_UI[order?.status] || {
      label: "Inconnu",
      cls: "bg-neutral-500/10 text-neutral-700 dark:text-neutral-300 border-neutral-500/20",
    };

  const total = (order.products || []).reduce((sum, p) => {
    const price = Number(p?.price) || 0;
    const qty = Number(p?.pivot?.quantity) || 0;
    return sum + price * qty;
  }, 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/60" onClick={onClose} />

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.97, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.97, y: 10 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden
              rounded-2xl border border-neutral-200/70 dark:border-neutral-800
              bg-white dark:bg-neutral-900 shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200/70 dark:border-neutral-800">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-neutral-900 text-white dark:bg-white dark:text-neutral-900 flex items-center justify-center">
                  <Package size={20} weight="bold" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                    Détails de la commande
                  </h2>
                  <p className="text-xs text-neutral-500">#{order.order_number}</p>
                </div>
              </div>

              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-600 dark:text-neutral-300"
                type="button"
                aria-label="Fermer"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto bg-neutral-50/60 dark:bg-neutral-950/40 flex-grow">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* LEFT */}
                <div className="space-y-4">
                  {/* Order Info */}
                  <div className="rounded-2xl border border-neutral-200/70 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Package size={18} />
                        <h3 className="font-semibold text-sm">Informations</h3>
                      </div>

                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${statusUI.cls}`}
                      >
                        {statusUI.label}
                      </span>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-neutral-500">Commande</span>
                        <span className="font-medium">{order.order_number}</span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-neutral-500 flex items-center gap-2">
                          <CalendarBlank size={16} /> Date
                        </span>
                        <span className="font-medium">{formatDate(order.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Customer */}
                  <div className="rounded-2xl text-start border border-neutral-200/70 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5">
                    <div className="flex items-center gap-2 mb-4">
                      <User size={18} />
                      <h3 className="font-semibold text-sm">Client</h3>
                    </div>

                    <div className="space-y-2 text-sm">
                      <p>
                        <strong>Nom :</strong> {order.first_name} {order.last_name}
                      </p>
                      <p>
                        <strong>Email :</strong> {order.email || "-"}
                      </p>
                      <p>
                        <strong>Téléphone :</strong> {order.phone || "-"}
                      </p>
                      <p>
                        <strong>Ville :</strong> {order.city || "-"}
                      </p>
                      {order.company_name && (
                        <p>
                          <strong>Société :</strong> {order.company_name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* RIGHT */}
                <div className="space-y-4 text-start">
                  {/* Products */}
                  <div className="rounded-2xl border border-neutral-200/70 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
                      <Package size={18} /> Produits
                    </h3>

                    <div className="space-y-3">
                      {(order.products || []).map((product) => {
                        const price = Number(product?.price) || 0;
                        const qty = Number(product?.pivot?.quantity) || 0;
                        const lineTotal = price * qty;

                        return (
                          <div
                            key={product.id}
                            className="flex items-center justify-between bg-neutral-50 dark:bg-neutral-800/40 rounded-xl p-3"
                          >
                            <div className="flex items-center gap-3">
                              <img
                                src={product?.images?.[0] || "/placeholder.svg"}
                                className="h-14 w-14 rounded-lg object-cover"
                                alt={product?.name || "Produit"}
                              />
                              <div>
                                <p className="font-medium text-sm">{product?.name}</p>
                                <p className="text-xs text-neutral-500">Qté : {qty}</p>
                              </div>
                            </div>

                            <div className="text-right text-sm">
                              <p>{formatMAD(price)}</p>
                              <p className="text-xs text-neutral-500">
                                {formatMAD(lineTotal)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Comments */}
                  {order.comments && (
                    <div className="rounded-2xl border border-neutral-200/70 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-5">
                      <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                        <ChatText size={18} /> Commentaires
                      </h3>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {order.comments}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-neutral-200/70 dark:border-neutral-800 px-6 py-4 bg-white dark:bg-neutral-900 flex justify-between items-center">
              <span className="font-medium">Total</span>
              <span className="text-2xl font-bold text-emerald-600">
                {formatMAD(total)}
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}